
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import PerformanceChart from './charts/PerformanceChart';
import { getBorderColorClass } from './utils/chartUtils';
import { Device } from '@/components/device-health/gis-map/types';

interface PerformanceMetricsChartProps {
  chartData: any[];
  selectedSection: string | null;
  onCardClick: (section: string | null) => void;
  sectionName: string;
  telemetryType: 'energy' | 'gas' | 'water';
  deviceId?: string;
  deviceData?: Device | null;
}

const PerformanceMetricsChart: React.FC<PerformanceMetricsChartProps> = ({
  chartData,
  selectedSection,
  onCardClick,
  sectionName,
  telemetryType,
  deviceId,
  deviceData
}) => {
  // State to track which lines are visible
  const [hiddenLines, setHiddenLines] = useState<string[]>([]);

  // Toggle visibility of a line when legend is clicked
  const handleLegendClick = (dataKey: string) => {
    setHiddenLines(prevHiddenLines => 
      prevHiddenLines.includes(dataKey)
        ? prevHiddenLines.filter(key => key !== dataKey)
        : [...prevHiddenLines, dataKey]
    );
  };

  // Get title based on telemetry type and device data
  const getTitle = () => {
    const baseTitle = (() => {
      switch (telemetryType) {
        case 'gas':
          return "Performance Metrics (m³/h, MB, ms)";
        case 'water':
          return "Performance Metrics (L/h, MB, ms)";
        default:
          return "Performance Metrics (W, MB, ms)";
      }
    })();

    if (deviceData) {
      return `${baseTitle}: ${deviceData.name}`;
    }
    
    return baseTitle;
  };

  // Filter chart data if a specific device ID is provided
  const filteredChartData = deviceId
    ? chartData.filter(item => {
        // This is a simplified filter - in a real app, you'd have a proper ID field
        // For now, we're just passing the data through as filtering would be specific to your data structure
        return true;
      })
    : chartData;

  return (
    <Card 
      className={cn(
        "mb-6",
        selectedSection === sectionName && getBorderColorClass(telemetryType)
      )}
    >
      <CardHeader className="pb-2 flex flex-row justify-between">
        <CardTitle className="text-lg">{getTitle()}</CardTitle>
        {selectedSection === sectionName && (
          <Button 
            variant="outline" 
            size="sm" 
            className="text-xs h-7"
            onClick={() => onCardClick(null)}
          >
            Clear selection
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <PerformanceChart 
          chartData={filteredChartData}
          hiddenLines={hiddenLines}
          onLegendClick={handleLegendClick}
          telemetryType={telemetryType}
        />
      </CardContent>
    </Card>
  );
};

export default PerformanceMetricsChart;
